"use client";

import Image from "next/image";
import Link from "next/link";
import { Trophy } from "lucide-react";
import { useState, useEffect, useRef, FormEvent } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import GraduateDetailsModal from "@/components/GraduateDetailsModal";
import { Graduate } from "@/types/graduate";

function GradCard({ graduate, onClick }: { graduate: Graduate; onClick: () => void }) {
  return (
    <div
      className="bg-white rounded-xl shadow-lg border-t-4 border-accent overflow-hidden w-44 cursor-pointer hover:shadow-xl transition-shadow duration-300"
      onClick={onClick}
    >
      <div className="relative h-28">
        {graduate.image ? (
          <Image
            src={graduate.image}
            alt={graduate.name}
            fill
            className="object-cover object-top"
          />
        ) : (
          <div className="w-full h-full bg-gray-100 flex items-center justify-center">
            <svg className="w-10 h-10 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
        )}
      </div>
      <div className="p-3 text-center">
        <p className="font-bold text-gray-900 text-sm truncate">{graduate.name}</p>
        <p className="text-accent text-xs truncate">{graduate.helicopter_type}</p>
      </div>
    </div>
  );
}

export default function Home() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
  });
  const [featuredGraduates, setFeaturedGraduates] = useState<Graduate[]>([]);
  const [selectedGraduate, setSelectedGraduate] = useState<Graduate | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const orbitContainerRef = useRef<HTMLDivElement>(null);
  const cardElemsRef = useRef<(HTMLDivElement | null)[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: "success" | "error" | null;
    message: string;
  }>({ type: null, message: "" });

  useEffect(() => {
    fetch("/api/graduates")
      .then((r) => (r.ok ? r.json() : []))
      .then((data: Graduate[]) => setFeaturedGraduates(data.slice(0, 5)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const N = featuredGraduates.length;
    if (N === 0) return;

    const CARD_W = 176;
    const PERIOD = 80000;
    let start: number | null = null;
    let rafId: number;

    const animate = (ts: number) => {
      const container = orbitContainerRef.current;
      if (!container) { rafId = requestAnimationFrame(animate); return; }

      if (start === null) {
        start = ts;
        cardElemsRef.current.forEach(el => { if (el) el.style.visibility = 'visible'; });
      }

      const { width, height } = container.getBoundingClientRect();
      const size = Math.min(width, height);
      const rx = size / 2 - CARD_W / 2 - 16;
      const ry = rx;
      const t = ts - start;

      for (let i = 0; i < N; i++) {
        const el = cardElemsRef.current[i];
        if (!el) continue;
        const phase = (2 * Math.PI * i) / N;
        const angle = (2 * Math.PI * t) / PERIOD + phase;
        const x = rx * Math.cos(angle);
        const y = ry * Math.sin(angle);
        el.style.transform = `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`;
      }

      rafId = requestAnimationFrame(animate);
    };

    rafId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafId);
  }, [featuredGraduates]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: "" });

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitStatus({
          type: "success",
          message: "Thank you! Your message has been sent successfully.",
        });
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          message: "",
        });
      } else {
        setSubmitStatus({
          type: "error",
          message: data.error || "Something went wrong. Please try again.",
        });
      }
    } catch {
      setSubmitStatus({
        type: "error",
        message: "Failed to send message. Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <Header />

      {/* Hero Section */}
      <div
        className="hero-background relative flex items-center justify-center min-h-screen bg-gradient-to-b from-gray-900 to-gray-800"
        style={{
          backgroundImage: "url(/images/background.webp)",
          backgroundSize: "cover",
          backgroundPosition: "center center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* Subtle overlay */}
        <div className="absolute inset-0 bg-black/40"></div>

        {/* Content */}
        <div className="relative text-center px-4 max-w-4xl mx-auto z-10">
          <div className="space-y-6 sm:space-y-8">
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-light text-white leading-tight">
              <span className="font-semibold text-accent">TopCat</span>{" "}
              Helicopters
            </h1>
            <p className="text-lg sm:text-xl lg:text-2xl text-white/90 font-light max-w-2xl mx-auto leading-relaxed px-2">
              Professional helicopter training and services
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center pt-2 sm:pt-4 px-4">
              <a
                href="#services"
                className="flex items-center justify-center px-6 py-3 sm:px-8 sm:py-4 bg-accent text-white font-medium rounded-full hover:bg-accent/90 transition-all duration-300 transform hover:scale-105 text-sm sm:text-base w-full sm:w-40"
              >
                Our Services
              </a>
              <a
                href="#contact"
                className="flex items-center justify-center px-6 py-3 sm:px-8 sm:py-4 border-2 border-white/30 text-white font-medium rounded-full backdrop-blur-sm hover:bg-white/10 transition-all duration-300 transform hover:scale-105 text-sm sm:text-base w-full sm:w-40"
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>

        {/* Minimal scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
          <a
            href="#services"
            className="hidden md:block text-white hover:text-white/80 transition-colors duration-300"
            aria-label="Scroll down to services section"
          >
            <svg
              className="w-6 h-6 animate-bounce"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 14l-7 7m0 0l-7-7m7 7V3"
              />
            </svg>
          </a>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Services Section */}
        <section
          id="services"
          className="py-16 sm:py-20 lg:py-24 bg-white scroll-mt-0"
        >
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-center text-gray-900 mb-8 sm:mb-12 px-4">
            Our Services
          </h2>

          {/* Services Grid */}
          <div className="px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 justify-items-center items-stretch">
              {/* Theory Courses */}
              <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border-t-4 border-accent overflow-hidden w-full max-w-sm flex flex-col h-full">
                {/* Large Image Header */}
                <div className="relative h-56">
                  <Image
                    src="/images/theory_courses.jpeg"
                    alt="Theory Courses"
                    fill
                    className="object-cover"
                  />
                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                </div>

                <div className="p-6 flex flex-col flex-grow">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    Theory Courses
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-3">
                    A thorough theoretical knowledge is a requirement for
                    understanding all aspects of helicopter flying.
                  </p>
                  <p className="text-gray-500 text-xs leading-relaxed mb-4">
                    In cooperation with Benedict Aviation and Blu Bird, distance
                    learning theory classes for Private Pilot certification start
                    at any moment of the year.
                    <br />
                    <a
                      href="https://benedictaviation.be/pplh/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent underline"
                    >
                      Benedict Aviation
                    </a>
                    {" "}—{" "}
                    <a
                      href="https://www.blubird.nl/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent underline"
                    >
                      Blu Bird
                    </a>
                    <br />
                    You can also follow classroom lessons at the Limburgse
                    Vleugels clubhouse at EBZW.
                  </p>
                  <div className="bg-accent/5 p-3 rounded-lg border border-accent/20 mt-auto">
                    <p className="text-accent font-medium text-sm">
                      Distance learning & classroom lessons
                    </p>
                  </div>
                </div>
              </div>

              {/* Sightseeing Flight */}
              <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border-t-4 border-accent overflow-hidden w-full max-w-sm flex flex-col h-full">
                {/* Large Image Header */}
                <div className="relative h-56">
                  <Image
                    src="/images/heli_above_44.png"
                    alt="Helicopter Above View"
                    fill
                    className="object-cover"
                  />
                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                  {/* Icon overlay */}
                  <div className="absolute bottom-4 left-4">
                    <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center">
                      <svg
                        className="w-6 h-6 text-accent"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="p-6 flex flex-col flex-grow">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    Sightseeing Flight
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-grow">
                    Experience helicopter sightseeing flights in our four-seat
                    Robinson R44.
                  </p>
                  <div className="bg-accent/5 p-3 rounded-lg border border-accent/20 mt-auto">
                    <p className="text-accent font-medium text-sm mb-1">
                      Contact Ann Lechat
                    </p>
                    <p className="text-gray-700 text-sm">
                      Call{" "}
                      <a
                        href="tel:+32475285193"
                        className="text-accent hover:text-accent/80 transition-colors font-medium"
                      >
                        +32 475 28 51 93
                      </a>{" "}
                      for availability
                    </p>
                  </div>
                </div>
              </div>

              {/* Flight Lessons  */}
              <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border-t-4 border-accent overflow-hidden w-full max-w-sm flex flex-col h-full">
                {/* Large Image Header */}
                <div className="relative h-56">
                  <Image
                    src="/images/approach_44.webp"
                    alt="Helicopter Approach Training"
                    fill
                    className="object-cover"
                  />
                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                  {/* Icon overlay */}
                  <div className="absolute bottom-4 left-4">
                    <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center">
                      <svg
                        className="w-6 h-6 text-accent"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="p-6 flex flex-col flex-grow">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    Private Pilot License
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-3">
                    Practical training on Robinson R22 and R44, Hughes (HU269)
                    or on Bell 47.
                  </p>
                  <p className="text-gray-500 text-xs leading-relaxed mb-4">
                    Training at Koksijde (EBFN), Genk/Zwartberg (EBZW), Tielen
                    Kasterlee (EBTK), and Zomergem (EBZM).
                  </p>
                  <div className="bg-accent/5 p-3 rounded-lg border border-accent/20 mt-auto">
                    <p className="text-accent font-medium text-sm">
                      Your first step into helicopter flying
                    </p>
                  </div>
                </div>
              </div>

              {/* Night Flying */}
              <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border-t-4 border-accent overflow-hidden w-full max-w-sm flex flex-col">
                {/* Large Image Header */}
                <div className="relative h-56">
                  <Image
                    src="/images/night_flight.png"
                    alt="Bell 47 Helicopter"
                    fill
                    className="object-cover"
                  />
                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                  {/* Icon overlay */}
                  <div className="absolute bottom-4 left-4">
                    <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center">
                      <svg
                        className="w-6 h-6 text-accent"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="p-6 flex flex-col flex-grow">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    VFR Night Flight Rating
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-3">
                    TopCat organizes VFR night rating theory and practical
                    training. Expand your flying capabilities with professional
                    night flight instruction.
                  </p>
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-900 text-sm mb-2">
                      Training Features:
                    </h4>
                    <div className="text-gray-600 text-xs space-y-1">
                      <span className="block">
                        • Comprehensive night flying theory
                      </span>
                      <span className="block">• Practical flight training</span>
                      <span className="block">• Safety procedures focus</span>
                    </div>
                  </div>
                  <div className="bg-accent/5 p-3 rounded-lg border border-accent/20 mt-auto">
                    <p className="text-accent font-medium text-sm">
                      Koksijde AB offers frequent training opportunities at no
                      additional cost.
                    </p>
                  </div>
                </div>
              </div>

              {/* Turbine Ratings */}
              <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border-t-4 border-accent overflow-hidden w-full max-w-sm flex flex-col">
                {/* Large Image Header */}
                <div className="relative h-56">
                  <Image
                    src="/images/OO-XDT.webp"
                    alt="Turbine Helicopter OO-XDT"
                    fill
                    className="object-cover"
                  />
                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                  {/* Icon overlay */}
                  <div className="absolute bottom-4 left-4">
                    <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center">
                      <svg
                        className="w-6 h-6 text-accent"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M13 10V3L4 14h7v7l9-11h-7z"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="p-6 flex flex-col flex-grow">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    Turbine Helicopter Type Rating
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-3">
                    A significant step up in your qualifications. Turbine
                    helicopters have more power, fly faster and require
                    additional training.
                  </p>
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-900 text-sm mb-2">
                      Available Aircraft:
                    </h4>
                    <div className="grid grid-cols-2 gap-1 text-gray-600 text-xs">
                      <span>• Robinson R66</span>
                      <span>• Bell 206</span>
                      <span>• Bell 47 Soloy</span>
                      <span>• EC120</span>
                      <span>• Gazelle SA341/SA342</span>
                    </div>
                  </div>
                  <div className="bg-accent/5 p-3 rounded-lg border border-accent/20 mt-auto">
                    <p className="text-accent text-xs leading-tight">
                      For AS350/EC130, AS355, Augusta A109: We cooperate with
                      Helicopter Services Ltd. (UK)
                    </p>
                  </div>
                </div>
              </div>

              {/* Flight Instructor and Commercial Pilot */}
              <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border-t-4 border-accent overflow-hidden w-full max-w-sm flex flex-col">
                {/* Large Image Header */}
                <div className="relative h-56">
                  <Image
                    src="/images/flight_instructor_commercial_pilot.jpeg"
                    alt="Flight Instructor and Commercial Pilot training in collaboration with FCH Aviation"
                    fill
                    className="object-cover"
                  />
                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                </div>

                <div className="p-6 flex flex-col flex-grow">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    Flight Instructor and Commercial Pilot
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-3">
                    Flight Instructor and Commercial Pilot training in
                    collaboration with FCH Aviation.
                  </p>
                  <p className="text-gray-500 text-xs leading-relaxed mb-4">
                    Theoretical training ATPL(H) or CPL(H).
                  </p>
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-900 text-sm mb-2">
                      Type Ratings:
                    </h4>
                    <div className="grid grid-cols-2 gap-1 text-gray-600 text-xs">
                      <span>• AS350</span>
                      <span>• AS355</span>
                      <span>• Agusta 109</span>
                    </div>
                  </div>
                  <div className="bg-accent/5 p-3 rounded-lg border border-accent/20 mt-auto">
                    <p className="text-accent font-medium text-sm">
                      In collaboration with FCH Aviation
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* FCH Aviation Collaboration */}
            <div className="mt-10 lg:mt-14 max-w-5xl mx-auto">
              <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border-t-4 border-accent overflow-hidden">
                <div className="flex flex-col md:flex-row">
                  {/* Text */}
                  <div className="p-6 sm:p-8 md:w-1/2 flex flex-col justify-center order-2 md:order-1">
                    <span className="text-accent font-semibold text-xs uppercase tracking-widest mb-3">
                      Partnership
                    </span>
                    <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-3">
                      In Collaboration with FCH Aviation
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-3">
                      Together with FCH Aviation Training we offer the
                      theoretical and practical Flight Instructor refresher and
                      renewal course, and training towards Flight Instructor
                      and Commercial Pilot.
                    </p>
                    <p className="text-gray-500 text-xs leading-relaxed mb-4">
                      Theoretical training ATPL(H) or CPL(H) &middot; Type
                      rating AS350, AS355 and Agusta 109.
                    </p>
                    <div className="bg-accent/5 p-3 rounded-lg border border-accent/20">
                      <p className="text-accent font-medium text-sm">
                        We wrote it together&hellip;
                      </p>
                    </div>
                  </div>

                  {/* Image */}
                  <div className="relative md:w-1/2 aspect-[3/2] md:aspect-auto order-1 md:order-2">
                    <Image
                      src="/images/fch_aviation.jpeg"
                      alt="In collaboration with FCH Aviation Training — theoretical and practical flight instructor refresher and renewal course"
                      fill
                      className="object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* About / Graduates Section */}
        <section id="about" className="flex items-center scroll-mt-16" style={{ minHeight: 'calc(100vh - 64px)' }}>
          <style>{`
            @keyframes marquee-scroll {
              from { transform: translateX(0); }
              to   { transform: translateX(-50%); }
            }
          `}</style>

          {/* Desktop: left text + right orbit */}
          <div className="hidden lg:flex items-center gap-16 w-full">

            {/* Left: text */}
            <div className="w-2/5 flex-shrink-0">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Trophy className="w-6 h-6 text-accent" />
                </div>
                <span className="text-accent font-semibold text-sm uppercase tracking-widest">Wall of Fame</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">
                Celebrate Our Graduates
              </h2>
              <p className="text-gray-600 leading-relaxed">
                With over 20 years of experience, TopCat Helicopters has
                trained and certified dozens of helicopter pilots. Each
                graduate represents years of dedication and passion for
                aviation. Click any graduate to learn more about their journey.
              </p>
            </div>

            {/* Right: circular orbit */}
            <div className="flex-1">
              <div
                ref={orbitContainerRef}
                className="relative w-full"
                style={{ height: 800 }}
              >
                {/* Dashed orbit ring */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="rounded-full" style={{
                    width: 510, height: 510,
                    border: '1.5px dashed rgba(180,0,50,0.18)',
                  }} />
                </div>

                {/* Center: See All Graduates button */}
                <div className="absolute inset-0 flex items-center justify-center z-10">
                  <Link
                    href="/wall-of-fame"
                    className="flex flex-col items-center justify-center w-24 h-24 bg-accent text-white font-medium rounded-full hover:bg-accent/90 transition-all duration-300 hover:scale-105 gap-1"
                  >
                    <Trophy className="w-5 h-5" />
                    <span className="text-xs">See All</span>
                  </Link>
                </div>

                {/* Cards — positioned by rAF */}
                {featuredGraduates.map((g, i) => (
                  <div
                    key={g.id}
                    ref={el => { cardElemsRef.current[i] = el; }}
                    className="absolute z-20"
                    style={{ left: '50%', top: '50%', visibility: 'hidden' }}
                  >
                    <GradCard
                      graduate={g}
                      onClick={() => { setSelectedGraduate(g); setIsModalOpen(true); }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Mobile: text then auto-scrolling marquee */}
          <div className="lg:hidden w-full">
            <div className="text-center mb-8 px-4">
              <div className="flex justify-center mb-4">
                <div className="w-14 h-14 bg-accent/10 rounded-full flex items-center justify-center">
                  <Trophy className="w-7 h-7 text-accent" />
                </div>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Celebrate Our Graduates
              </h2>
              <p className="text-gray-600 mb-8 text-sm leading-relaxed">
                With over 20 years of experience, TopCat Helicopters has
                trained and certified dozens of helicopter pilots.
              </p>
              <Link
                href="/wall-of-fame"
                className="inline-flex items-center justify-center px-8 py-4 bg-accent text-white font-medium rounded-full hover:bg-accent/90 transition-all duration-300 transform hover:scale-105"
              >
                <Trophy className="w-5 h-5 mr-2" />
                See All Graduates
              </Link>
            </div>

            {featuredGraduates.length > 0 && (
              <div className="overflow-hidden mt-6">
                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  width: 'max-content',
                  animation: 'marquee-scroll 14s linear infinite',
                }}>
                  {[...featuredGraduates, ...featuredGraduates].map((g, i) => (
                    <div key={`${g.id}-${i}`} style={{ flexShrink: 0 }}>
                      <GradCard
                        graduate={g}
                        onClick={() => { setSelectedGraduate(g); setIsModalOpen(true); }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Meet The Team Section */}
        <section id="team" className="py-24 bg-white scroll-mt-0">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Meet The Team
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Our experienced team of certified flight instructors and
                aviation professionals are dedicated to providing the highest
                quality helicopter training and services.
              </p>
            </div>

            {/* Ann Lechat - Full Width Row */}
            <div className="px-4 mb-4 sm:mb-6 lg:mb-8">
              {/* Team Member 1 - Ann Lechat */}
              <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border-t-4 border-accent p-6 sm:p-8">
                <div className="flex flex-col md:flex-row gap-6 items-center md:items-start">
                  <div className="relative w-32 h-32 sm:w-40 sm:h-40 flex-shrink-0">
                    <Image
                      src="/images/team/ann_lechat.webp"
                      alt="Ann Lechat"
                      fill
                      className="object-cover rounded-full"
                    />
                  </div>
                  <div className="flex-1 text-center md:text-left w-full">
                    <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                      Ann Lechat
                    </h3>
                    <p className="text-accent font-medium text-base mb-4">
                      EASA and FAA Flight instructor
                    </p>
                    <div className="text-gray-600 text-sm leading-relaxed space-y-3">
                      <div className="space-y-1">
                        <p className="font-medium text-gray-800">EASA Flight Examiner</p>
                        <p className="font-medium text-gray-800">EASA Flight Instructor Examiner</p>
                        <p className="font-medium text-gray-800">Head of training DTO</p>
                        <p className="font-medium text-gray-800">EASA and FAA Aviation Medical Examiner</p>
                      </div>
                      
                      <div className="mt-4">
                        <p className="font-semibold text-gray-900 mb-4 text-base">
                          Instruction Aircraft:
                        </p>
                        
                        <div className="grid md:grid-cols-2 gap-6 max-w-md mx-auto md:max-w-none">
                          {/* EASA Instructor */}
                          <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 p-4 rounded-lg border border-blue-200 text-left">
                            <p className="font-semibold text-blue-900 mb-3 text-sm flex items-center">
                              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/>
                                <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd"/>
                              </svg>
                              EASA Instructor
                            </p>
                            <ul className="space-y-1.5 text-xs text-gray-700">
                              <li className="flex items-start">
                                <span className="text-blue-600 mr-2 mt-0.5">✓</span>
                                <span>Robinson R22/R44/R66</span>
                              </li>
                              <li className="flex items-start">
                                <span className="text-blue-600 mr-2 mt-0.5">✓</span>
                                <span>EC120</span>
                              </li>
                              <li className="flex items-start">
                                <span className="text-blue-600 mr-2 mt-0.5">✓</span>
                                <span>AS350/EC130</span>
                              </li>
                              <li className="flex items-start">
                                <span className="text-blue-600 mr-2 mt-0.5">✓</span>
                                <span>Bell 206 (all variants)</span>
                              </li>
                              <li className="flex items-start">
                                <span className="text-blue-600 mr-2 mt-0.5">✓</span>
                                <span>Bell 47/B47 Soloy</span>
                              </li>
                              <li className="flex items-start">
                                <span className="text-blue-600 mr-2 mt-0.5">✓</span>
                                <span>Gazelle SA341/SA342</span>
                              </li>
                              <li className="flex items-start">
                                <span className="text-blue-600 mr-2 mt-0.5">✓</span>
                                <span>HU369/MD500N/MD600N</span>
                              </li>
                              <li className="flex items-start">
                                <span className="text-blue-600 mr-2 mt-0.5">✓</span>
                                <span>Hughes HU269</span>
                              </li>
                              <li className="flex items-start">
                                <span className="text-blue-600 mr-2 mt-0.5">✓</span>
                                <span>Alouette SE313/SA318</span>
                              </li>
                            </ul>
                          </div>

                          {/* FAA Instructor */}
                          <div className="bg-gradient-to-br from-green-50 to-green-100/50 p-4 rounded-lg border border-green-200 text-left">
                            <p className="font-semibold text-green-900 mb-3 text-sm flex items-center">
                              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/>
                                <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd"/>
                              </svg>
                              FAA Instructor
                            </p>
                            <ul className="space-y-1.5 text-xs text-gray-700">
                              <li className="flex items-start">
                                <span className="text-green-600 mr-2 mt-0.5">✓</span>
                                <span>Robinson R22/R44/R66</span>
                              </li>
                              <li className="flex items-start">
                                <span className="text-green-600 mr-2 mt-0.5">✓</span>
                                <span>EC120</span>
                              </li>
                              <li className="flex items-start">
                                <span className="text-green-600 mr-2 mt-0.5">✓</span>
                                <span>AS350/EC130</span>
                              </li>
                              <li className="flex items-start">
                                <span className="text-green-600 mr-2 mt-0.5">✓</span>
                                <span>Bell 206 (all variants)</span>
                              </li>
                              <li className="flex items-start">
                                <span className="text-green-600 mr-2 mt-0.5">✓</span>
                                <span>Bell 47/B47 Soloy</span>
                              </li>
                              <li className="flex items-start">
                                <span className="text-green-600 mr-2 mt-0.5">✓</span>
                                <span>Bell UH-1 Huey</span>
                              </li>
                              <li className="flex items-start">
                                <span className="text-green-600 mr-2 mt-0.5">✓</span>
                                <span>Augusta A119 Koala</span>
                              </li>
                              <li className="flex items-start">
                                <span className="text-green-600 mr-2 mt-0.5">✓</span>
                                <span>Gazelle SA341/SA342</span>
                              </li>
                              <li className="flex items-start">
                                <span className="text-green-600 mr-2 mt-0.5">✓</span>
                                <span>HU369/MD500N/MD600N</span>
                              </li>
                              <li className="flex items-start">
                                <span className="text-green-600 mr-2 mt-0.5">✓</span>
                                <span>Hughes HU269</span>
                              </li>
                              <li className="flex items-start">
                                <span className="text-green-600 mr-2 mt-0.5">✓</span>
                                <span>Alouette SE313/SA318</span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Other Team Members Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 px-4">
              {/* Removed Ann Lechat from here */}

              {/* Team Member 2 - Karel Kinabel */}
              <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border-t-4 border-accent text-center p-6 sm:p-8 flex flex-col">
                <div className="relative w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 mx-auto mb-4 sm:mb-6">
                  <Image
                    src="/images/team/karel_kinabel.webp"
                    alt="Karel Kinabel"
                    fill
                    className="object-cover rounded-full"
                  />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Karel Kinabel
                </h3>
                <p className="text-accent font-medium text-sm mb-4">
                  Flight Instructor
                </p>
                <div className="text-gray-600 text-sm leading-relaxed space-y-3 flex-grow flex flex-col">
                  <p>
                    Former Belgian Airforce Sea King and NH90 pilot and
                    instructor. Mostly available during weekdays.
                  </p>
                  <div className="mt-auto">
                    <p className="font-semibold text-gray-800 mb-3 text-sm">
                      Instruction Aircraft:
                    </p>
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 p-3 rounded-lg border border-blue-200">
                      <ul className="space-y-1.5 text-xs text-gray-700">
                        <li className="flex items-start">
                          <span className="text-blue-600 mr-2 mt-0.5">✓</span>
                          <span>Robinson R44</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-blue-600 mr-2 mt-0.5">✓</span>
                          <span>Bell 47</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Team Member 3 - Benjamin De Kaste */}
              <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border-t-4 border-accent text-center p-6 sm:p-8 flex flex-col">
                <div className="relative w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 mx-auto mb-4 sm:mb-6">
                  <Image
                    src="/images/team/benjamin_de_kaste.webp"
                    alt="Benjamin De Kaste"
                    fill
                    className="object-cover rounded-full"
                  />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Benjamin De Kaste
                </h3>
                <p className="text-accent font-medium text-sm mb-4">
                  Flight Instructor
                </p>
                <div className="text-gray-600 text-sm leading-relaxed space-y-3 flex-grow flex flex-col">
                  <p>
                    Helicopter pilot Airbus H145. Check reservation system for
                    availability.
                  </p>
                  <div className="mt-auto">
                    <p className="font-semibold text-gray-800 mb-3 text-sm">
                      Instruction Aircraft:
                    </p>
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 p-3 rounded-lg border border-blue-200">
                      <ul className="space-y-1.5 text-xs text-gray-700">
                        <li className="flex items-start">
                          <span className="text-blue-600 mr-2 mt-0.5">✓</span>
                          <span>Robinson R22 / R44</span>
                        </li>
                        <li>
                          <span className="text-blue-600 mr-2 mt-0.5"></span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Team Member 4 - Jeff Keuppens */}
              <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border-t-4 border-accent text-center p-6 sm:p-8 flex flex-col">
                <div className="relative w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 mx-auto mb-4 sm:mb-6">
                  <Image
                    src="/images/team/jeff_keuppens.webp"
                    alt="Jeff Keuppens"
                    fill
                    className="object-cover rounded-full"
                  />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Jef Keuppens
                </h3>
                <p className="text-accent font-medium text-sm mb-4">
                  Instructor
                </p>
                <div className="text-gray-600 text-sm leading-relaxed space-y-3 flex-grow flex flex-col">
                  <p>
                    Helicopter pilot Eurocopter Écureuil (AS350) and Bell UH-1
                    Iroquois (Huey). Check reservation system for availability.
                  </p>
                  <div className="mt-auto">
                    <p className="font-semibold text-gray-800 mb-3 text-sm">
                      Instruction Aircraft:
                    </p>
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 p-3 rounded-lg border border-blue-200">
                      <ul className="space-y-1.5 text-xs text-gray-700">
                        <li className="flex items-start">
                          <span className="text-blue-600 mr-2 mt-0.5">✓</span>
                          <span>Hughes HU269</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-blue-600 mr-2 mt-0.5">✓</span>
                          <span>Bell 47</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Team Member 5 - Ben Vanhentenrijk */}
              <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border-t-4 border-accent text-center p-6 sm:p-8 flex flex-col">
                <div className="relative w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 mx-auto mb-4 sm:mb-6">
                  <Image
                    src="/images/team/ben_vanhentenrijk.webp"
                    alt="Ben Vanhentenrijk"
                    fill
                    className="object-cover rounded-full"
                  />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Ben Vanhentenrijk
                </h3>
                <p className="text-accent font-medium text-sm mb-4">
                  Instructor
                </p>
                <div className="text-gray-600 text-sm leading-relaxed space-y-3 flex-grow flex flex-col">
                  <p>
                    Helicopter pilot Airbus H135. Check reservation system for
                    availability.
                  </p>
                  <div className="mt-auto">
                    <p className="font-semibold text-gray-800 mb-3 text-sm">
                      Instruction Aircraft:
                    </p>
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 p-3 rounded-lg border border-blue-200">
                      <ul className="space-y-1.5 text-xs text-gray-700">
                        <li className="flex items-start">
                          <span className="text-blue-600 mr-2 mt-0.5">✓</span>
                          <span>Robinson R22 / R44</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-blue-600 mr-2 mt-0.5">✓</span>
                          <span>Guimbal Cabri G2</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Team Member 6 - Wim Declerq */}
              <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border-t-4 border-accent text-center p-6 sm:p-8 flex flex-col">
                <div className="relative w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 mx-auto mb-4 sm:mb-6">
                  <Image
                    src="/images/team/wim_declerq.webp"
                    alt="Wim Declerq"
                    fill
                    className="object-cover rounded-full"
                  />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Wim Declerq
                </h3>
                <p className="text-accent font-medium text-sm mb-4">
                  Instructor
                </p>
                <div className="text-gray-600 text-sm leading-relaxed space-y-3 flex-grow flex flex-col">
                  <p>
                    Helicopter pilot Airbus H135 and EC120. Check reservation
                    system for availability.
                  </p>
                  <div className="mt-auto">
                    <p className="font-semibold text-gray-800 mb-3 text-sm">
                      Instruction Aircraft:
                    </p>
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 p-3 rounded-lg border border-blue-200">
                      <ul className="space-y-1.5 text-xs text-gray-700">
                        <li className="flex items-start">
                          <span className="text-blue-600 mr-2 mt-0.5">✓</span>
                          <span>Robinson R44</span>
                        </li>
                        <li>
                          <span className="text-blue-600 mr-2 mt-0.5"></span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Team Member 7 - Xavier de Tracy */}
              <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border-t-4 border-accent text-center p-6 sm:p-8 flex flex-col">
                <div className="relative w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 mx-auto mb-4 sm:mb-6">
                  <Image
                    src="/images/team/xavier_de_tracy.webp"
                    alt="Xavier de Tracy"
                    fill
                    className="object-cover rounded-full"
                  />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Xavier de Tracy
                </h3>
                <p className="text-accent font-medium text-sm mb-4">
                  Ground Instructor
                </p>
                <div className="text-gray-600 text-sm leading-relaxed space-y-3 flex-grow flex flex-col">
                  <p>
                    Helicopter pilot Écureuil (AS350), EC130 and Gazelle
                    (SA341/SA342).
                  </p>
                  <p>Aircraft airframe and powerplant mechanic</p>
                  <div className="mt-auto">
                    <p className="font-semibold text-gray-800 mb-2">
                      Instruction:
                    </p>
                    <div className="text-xs text-gray-700 space-y-1">
                      <div>Ground</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Team Member 8 - Paul Lejeune */}
              <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border-t-4 border-accent text-center p-6 sm:p-8 flex flex-col">
                <div className="relative w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 mx-auto mb-4 sm:mb-6">
                  <Image
                    src="/images/team/paul_lejeune.webp"
                    alt="Paul Lejeune"
                    fill
                    className="object-cover rounded-full"
                  />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Paul Lejeune
                </h3>
                <p className="text-accent font-medium text-sm mb-4">
                  Flight Instructor
                </p>
                <div className="text-gray-600 text-sm leading-relaxed space-y-3 flex-grow flex flex-col">
                  <p>
                    Flight instructor specializing in helicopter pilot training
                    and safety procedures.
                  </p>
                  <div className="mt-auto">
                    <p className="font-semibold text-gray-800 mb-3 text-sm">
                      Instruction Aircraft:
                    </p>
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 p-3 rounded-lg border border-blue-200">
                      <ul className="space-y-1.5 text-xs text-gray-700">
                        <li className="flex items-start">
                          <span className="text-blue-600 mr-2 mt-0.5">✓</span>
                          <span>Robinson R22</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-blue-600 mr-2 mt-0.5">✓</span>
                          <span>Bell 47</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section
          id="contact"
          className="py-16 sm:py-20 lg:py-24 bg-white scroll-mt-0"
        >
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-center text-gray-800 mb-8 sm:mb-12">
              Contact Us
            </h2>
            <div className="flex flex-col lg:grid lg:grid-cols-2 gap-6 lg:gap-10">
              {/* Contact Information Widgets - Left Side */}
              <div className="space-y-4 order-2 lg:order-1">
                {/* Email Widget */}
                <a href="mailto:topcathelicopters@gmail.com" className="block">
                  <div className="bg-white p-4 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300 cursor-pointer">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mr-4">
                        <svg
                          className="w-6 h-6 text-accent"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M3 8l7.89 7.89a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600 mb-1">
                          Email
                        </p>
                        <p className="text-gray-900 font-semibold hover:text-accent transition-colors duration-200">
                          topcathelicopters@gmail.com
                        </p>
                      </div>
                    </div>
                  </div>
                </a>

                {/* Phone Widget */}
                <a href="tel:+32475285193" className="block">
                  <div className="bg-white p-4 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300 cursor-pointer">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mr-4">
                        <svg
                          className="w-6 h-6 text-accent"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600 mb-1">
                          Phone
                        </p>
                        <p className="text-gray-900 font-semibold hover:text-accent transition-colors duration-200">
                          +32 475 28 51 93
                        </p>
                      </div>
                    </div>
                  </div>
                </a>

                {/* Location Widget */}
                <div className="bg-white p-4 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mr-4 mt-1">
                      <svg
                        className="w-6 h-6 text-accent"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600 mb-2">
                        Operating Locations
                      </p>
                      <div className="grid grid-cols-1 gap-1 text-sm text-gray-700">
                        <span>• Koksijde (EBFN)</span>
                        <span>• Genk/Zwartberg (EBZW)</span>
                        <span>• Tielen Kasterlee (EBTK)</span>
                        <span>• Zomergem (EBZM)</span>
                        <span>• Disksmuide/Leke (EBDV)</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form Widget - Right Side */}
              <div className="bg-white p-5 sm:p-6 lg:p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border-t-4 border-accent order-1 lg:order-2">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-5">
                  Send us a Message
                </h3>
                <form className="space-y-5" onSubmit={handleSubmit}>
                  {/* Success/Error Messages */}
                  {submitStatus.type && (
                    <div
                      className={`p-4 rounded-lg ${
                        submitStatus.type === "success"
                          ? "bg-green-50 text-green-800 border border-green-200"
                          : "bg-red-50 text-red-800 border border-red-200"
                      }`}
                    >
                      {submitStatus.message}
                    </div>
                  )}

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label
                        htmlFor="firstName"
                        className="block text-sm font-medium text-gray-700 mb-2.5"
                      >
                        First Name *
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                        disabled={isSubmitting}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent outline-none transition-colors placeholder-gray-500 text-black disabled:bg-gray-100 disabled:cursor-not-allowed"
                        placeholder="Enter your first name"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="lastName"
                        className="block text-sm font-medium text-gray-700 mb-2.5"
                      >
                        Last Name *
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                        disabled={isSubmitting}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent outline-none transition-colors placeholder-gray-500 text-black disabled:bg-gray-100 disabled:cursor-not-allowed"
                        placeholder="Enter your last name"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 mb-2.5"
                    >
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      disabled={isSubmitting}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent outline-none transition-colors placeholder-gray-500 text-black disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="Enter your email address"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-medium text-gray-700 mb-2.5"
                    >
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      disabled={isSubmitting}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent outline-none transition-colors placeholder-gray-500 text-black disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="Enter your phone number"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-gray-700 mb-2.5"
                    >
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      disabled={isSubmitting}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent outline-none transition-colors resize-vertical placeholder-gray-500 text-black disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="Tell us about your training needs or questions..."
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-accent text-white py-3 px-6 rounded-lg hover:bg-opacity-90 transition-all duration-300 font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <Footer />

      <GraduateDetailsModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setSelectedGraduate(null); }}
        graduate={selectedGraduate}
      />
    </div>
  );
}
